/**
 * Kosten & Gewinn je Druckvariante — reine Rechenhilfen (keine Schreibzugriffe).
 * Preise: settings/printVariantPrices (Shopify-Sync, Länderpreise aus Bestellungen, manuelle Überschreibung),
 * Kosten: settings/printVariants + settings/productTypes (unverändert, nur gelesen).
 */
export const BASE_COUNTRIES = ["DE", "AT", "NL", "BE", "LU", "ES", "FR", "PL", "US", "CA"];

/** Mehrwertsteuer-Standardsätze (Prozent) — Länder ohne Eintrag: 0, in den Annahmen änderbar */
export const DEFAULT_VAT = {
  DE: 19, AT: 20, NL: 21, BE: 21, LU: 17, ES: 21, FR: 20, PL: 23, IT: 22, PT: 23, IE: 23, DK: 25, SE: 25, FI: 25.5,
  NO: 25, CZ: 21, SK: 20, SI: 22, HR: 25, HU: 27, RO: 19, BG: 20, GR: 24, EE: 22, LV: 21, LT: 21, CY: 19, MT: 18,
  CH: 8.1, LI: 8.1, GB: 20, US: 0, CA: 0, AU: 10, NZ: 15, JP: 10,
};

export const DEFAULT_CONFIG = {
  vat: DEFAULT_VAT,
  // Zahlungsgebühr wie Shopify Payments EUR: 2,1 % + 0,30
  paymentRate: 2.1,
  paymentFixed: 0.3,
  // Welcher Shop den Listenpreis liefert, wenn es keinen Länderpreis aus Bestellungen gibt
  countryShop: { DE: "AQ_DE", AT: "AQ_DE", NL: "AQ_DE", BE: "AQ_DE", LU: "AQ_DE", PL: "AQ_DE", ES: "AQ_ES", FR: "AQ_FR", US: "OH_US", CA: "OH_US" },
};

export const mergeConfig = (stored) => ({
  vat: { ...DEFAULT_CONFIG.vat, ...((stored && stored.vat) || {}) },
  paymentRate: stored && stored.paymentRate !== undefined ? Number(stored.paymentRate) : DEFAULT_CONFIG.paymentRate,
  paymentFixed: stored && stored.paymentFixed !== undefined ? Number(stored.paymentFixed) : DEFAULT_CONFIG.paymentFixed,
  countryShop: { ...DEFAULT_CONFIG.countryShop, ...((stored && stored.countryShop) || {}) },
});

/** Shop eines Landes: Länderpreis-Quelle › Konfiguration › DE-Shop */
export const shopFor = (entry, cc, config) =>
  (entry && entry.countryPrice && entry.countryPrice[cc] && entry.countryPrice[cc].shopKey) || config.countryShop[cc] || "AQ_DE";

/** Betrag in EUR (Kurse wie Statistik: settings/statistics.exchangeRates, eur = 1) */
export const toEur = (amount, currency, rates) => amount / (Number((rates || {})[String(currency || "eur").toLowerCase()]) || 1);

/**
 * Verkaufspreis (brutto) einer Variante für ein Land, in EUR + Original:
 * manuell › Länderpreis aus Bestellungen (Kundenwährung) › Listenpreis des Shops › häufigster Bestellpreis des Shops
 */
export const priceFor = (entry, cc, config, rates) => {
  const manual = entry && entry.manual && entry.manual[cc];
  if (manual !== undefined && manual !== null && manual !== "" && Number(manual) > 0) return { gross: Number(manual), raw: Number(manual), currency: "EUR", source: "manuell" };
  // Markt mit fester Preisliste (Shopify Markets): exakter Listen- und Streichpreis in Marktwährung
  const mp = entry && entry.marketPrice && entry.marketPrice[cc];
  if (mp && Number(mp.price) > 0) {
    const compareAtM = Number(mp.compareAt) > 0 ? { raw: Number(mp.compareAt), currency: mp.currency, gross: toEur(Number(mp.compareAt), mp.currency, rates) } : null;
    return { gross: toEur(Number(mp.price), mp.currency, rates), raw: Number(mp.price), currency: mp.currency, source: `Preisliste ${mp.market}`, shopKey: mp.shopKey, market: mp.market, priceListId: mp.priceListId, compareAt: compareAtM };
  }
  const shopKey = shopFor(entry, cc, config);
  const s = entry && entry.shopify && entry.shopify[shopKey];
  const compareAt = s && Number(s.compareAt) > 0 ? { raw: Number(s.compareAt), currency: s.currency, gross: toEur(Number(s.compareAt), s.currency, rates) } : null;
  const c = entry && entry.countryPrice && entry.countryPrice[cc];
  if (c && Number(c.price) > 0) return { gross: toEur(Number(c.price), c.currency, rates), raw: Number(c.price), currency: c.currency, source: `Bestellungen ${cc}`, n: c.n, shopKey, compareAt };
  if (s && Number(s.price) > 0) return { gross: toEur(Number(s.price), s.currency, rates), raw: Number(s.price), currency: s.currency, source: shopKey, shopKey, compareAt };
  const o = entry && entry.ordersPrice && entry.ordersPrice[shopKey];
  if (o && Number(o.price) > 0) return { gross: toEur(Number(o.price), o.currency, rates), raw: Number(o.price), currency: o.currency, source: `${shopKey} (Bestellungen)`, shopKey, compareAt };
  return { gross: null, source: null, shopKey, compareAt };
};

/** Fulfiller-Kosten der Variante für ein Land (Standard-Fulfiller laut fulfillRank) */
export const costsFor = (variant, cc) => {
  const rank = (variant.fulfillRank && (variant.fulfillRank[cc] || variant.fulfillRank.default)) || [];
  const fk = rank[0];
  const f = fk && variant.fulfiller ? variant.fulfiller[fk] : null;
  if (!f) return { fulfiller: null, production: 0, shipping: 0 };
  const ship = f.shippingCosts || {};
  return { fulfiller: fk, production: Number(f.costs) || 0, shipping: Number(ship[cc] !== undefined ? ship[cc] : ship.default) || 0 };
};

/** Marge aus Bruttopreis (EUR) und Kosten */
export const marginFor = (gross, cc, config, c, employee) => {
  const vat = Number(config.vat[cc]) || 0;
  const net = gross !== null ? gross / (1 + vat / 100) : null;
  const payment = gross !== null ? (gross * config.paymentRate) / 100 + config.paymentFixed : 0;
  const costs = c.production + c.shipping + (Number(employee) || 0) + payment;
  const margin = net !== null ? net - costs : null;
  return { net, payment, costs, margin, marginPct: margin !== null && net ? (margin / net) * 100 : null };
};

/** Eine Tabellenzeile: Preis, Streichpreis, Kosten, Marge, Verkäufe; optional Testpreis (Shop-Währung) */
export const computeRow = ({ variant, entry, cc, config, rates, employee, sales, testPrice, testCompareAt }) => {
  const price = priceFor(entry, cc, config, rates);
  const c = costsFor(variant, cc);
  const m = marginFor(price.gross, cc, config, c, employee);
  const s = sales || null;
  let contribution = null;
  const vat = Number(config.vat[cc]) || 0;
  if (s && s.units) {
    const netActual = s.grossEur / (1 + vat / 100);
    contribution = netActual - s.units * (c.production + c.shipping + (Number(employee) || 0)) - ((s.grossEur * config.paymentRate) / 100 + s.units * config.paymentFixed);
  }
  // Simulation: Testpreis in der Währung der Preisquelle → EUR → neue Marge, Δ je Stück, Δ Deckungsbeitrag bei gleicher Stückzahl
  let test = null;
  if (testPrice !== undefined && testPrice !== null && testPrice !== "" && Number(testPrice) > 0) {
    const grossT = toEur(Number(testPrice), price.currency || "EUR", rates);
    const mt = marginFor(grossT, cc, config, c, employee);
    const deltaUnit = m.margin !== null && mt.margin !== null ? mt.margin - m.margin : null;
    test = { gross: grossT, raw: Number(testPrice), compareAt: testCompareAt !== undefined && testCompareAt !== "" && testCompareAt !== null ? Number(testCompareAt) : null, ...mt, deltaUnit, deltaContribution: deltaUnit !== null && s ? deltaUnit * s.units : null };
  }
  return {
    key: variant.key,
    name: variant.name,
    gross: price.gross,
    raw: price.raw,
    currency: price.currency,
    compareAt: price.compareAt,
    priceSource: price.source,
    shopKey: price.shopKey,
    market: price.market || null,
    fulfiller: c.fulfiller,
    production: c.production,
    shipping: c.shipping,
    employee: Number(employee) || 0,
    payment: m.payment,
    costs: m.costs,
    net: m.net,
    margin: m.margin,
    marginPct: m.marginPct,
    units: s ? s.units : 0,
    grossEur: s ? s.grossEur : 0,
    contribution,
    test,
  };
};
